import { completeAction, startAction, updateAction } from "./logger";

const getCorrelationId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `corr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getActionIdFromResponse = (response) =>
  response?.action_id || response?.actionId || response?.id || response?.data?.id;

export const createProductFlowLogger = ({
  companyId,
  userId,
  entityId = null,
}) => {
  const correlationId = getCorrelationId();
  let actionId = null;
  let actionStarted = false;

  const basePayload = {
    company_id: companyId,
    user_id: userId,
    action_type: "PRODUCT_LISTING",
    module: "PRODUCT",
    entity_type: "PRODUCT",
    entity_id: entityId,
    source: "WEB",
    correlation_id: correlationId,
  };

  const ensureStarted = async (initialPayload = {}) => {
    if (actionStarted) return actionId;

    try {
      const response = await startAction({
        ...basePayload,
        ...initialPayload,
      });

      actionId = getActionIdFromResponse(response);
      actionStarted = Boolean(actionId);
    } catch (error) {
      console.error("Failed to start product flow action:", error);
    }

    return actionId;
  };

  const logStep = async ({ progress, currentStep, entity_id, extra = {} }) => {
    await ensureStarted();
    if (!actionId) return;

    try {
      await updateAction(actionId, {
        progress,
        current_step: currentStep,
        entity_id: entity_id ?? basePayload.entity_id,
        ...extra,
      });
    } catch (error) {
      console.error("Failed to update product flow action:", error);
    }
  };

  const complete = async ({ status, progress, currentStep, entity_id, error }) => {
    await ensureStarted();
    if (!actionId) return;

    try {
      await completeAction(actionId, {
        status,
        progress,
        current_step: currentStep,
        entity_id: entity_id ?? basePayload.entity_id,
        ...(error ? { error_message: error } : {}),
      });
    } catch (completeError) {
      console.error("Failed to complete product flow action:", completeError);
    }
  };

  return {
    correlationId,
    startUpload: () =>
      ensureStarted({
        progress: 10,
        current_step: "UPLOAD",
      }),
    logAutofill: () =>
      logStep({
        progress: 30,
        currentStep: "AUTOFILL",
      }),
    logEbayAutofill: () =>
      logStep({
        progress: 40,
        currentStep: "EBAY_AUTOFILL",
      }),
    logUpscale: () =>
      logStep({
        progress: 60,
        currentStep: "UPSCALE",
      }),
    logBackgroundRemoval: () =>
      logStep({
        progress: 70,
        currentStep: "BACKGROUND_REMOVAL",
      }),
    logCrossListing: (selectedMarketplaces = []) =>
      logStep({
        progress: 90,
        currentStep: "CROSS_LISTING",
        extra: { marketplaces: selectedMarketplaces },
      }),
    completeSuccess: ({ entityId: completedEntityId } = {}) =>
      complete({
        status: "SUCCESS",
        progress: 100,
        currentStep: "COMPLETED",
        entity_id: completedEntityId,
      }),
    completeFailed: ({ error, entityId: completedEntityId } = {}) =>
      complete({
        status: "FAILED",
        progress: 100,
        currentStep: "FAILED",
        entity_id: completedEntityId,
        error,
      }),
  };
};
